/**
 * Replaces "You/Your" with "The patient/The patient's" for third-party mode.
 * Handles basic capitalization based on the match.
 */
export function convertTextForThirdParty(text: string | null | undefined, locale = 'en'): string {
  if (!text) return '';

  let converted = text;

  if (locale === 'ko') {
    // Korean replacements
    // "당신" or "본인" might be used, though less common in medical forms.
    // Replacing "당신" -> "환자분"
    // Replacing "본인" -> "환자분" if it refers to "self"
    converted = converted.replace(/당신은/g, '환자분은');
    converted = converted.replace(/당신의/g, '환자분의');
    converted = converted.replace(/당신을/g, '환자분을');
    converted = converted.replace(/당신에게/g, '환자분에게');
    converted = converted.replace(/당신/g, '환자분');

    // "귀하" might be used in formal texts
    converted = converted.replace(/귀하는/g, '환자분은');
    converted = converted.replace(/귀하의/g, '환자분의');
    converted = converted.replace(/귀하/g, '환자분');

    // Sometimes "어떻게 되십니까?" (How are you?) implies subject.
    // Hard to replace implicit subjects without NLP.
  } else if (locale === 'ja') {
    // Japanese replacements
    // "あなた" (anata) -> "患者様" (kanjasama - polite term for patient)
    converted = converted.replace(/あなたは/g, '患者様は');
    converted = converted.replace(/あなたの/g, '患者様の');
    converted = converted.replace(/あなたを/g, '患者様を');
    converted = converted.replace(/あなたに/g, '患者様に');
    converted = converted.replace(/あなた/g, '患者様');

    // "ご自身" (gojishin - yourself) -> "患者様"
    converted = converted.replace(/ご自身は/g, '患者様は');
    converted = converted.replace(/ご自身の/g, '患者様の');
    converted = converted.replace(/ご自身/g, '患者様');

    // "お客様" (okyakusama - customer/guest) -> "患者様" if used in medical context
    converted = converted.replace(/お客様は/g, '患者様は');
    converted = converted.replace(/お客様の/g, '患者様の');
    converted = converted.replace(/お客様/g, '患者様');
  } else if (locale === 'es') {
    // Spanish replacements
    // "Tú/Tu" (informal) and "Usted/Su" (formal) -> "El paciente/El paciente's"

    // Formal "Usted" -> "El paciente"
    converted = converted.replace(/\bUsted\b/g, 'El paciente');
    converted = converted.replace(/\busted\b/g, 'el paciente');

    // Formal possessive "Su/Sus" -> "del paciente"
    converted = converted.replace(/\bSu\b/g, 'Del paciente');
    converted = converted.replace(/\bsu\b/g, 'del paciente');
    converted = converted.replace(/\bSus\b/g, 'Del paciente');
    converted = converted.replace(/\bsus\b/g, 'del paciente');

    // Informal "Tú" -> "El paciente"
    converted = converted.replace(/\bTú\b/g, 'El paciente');
    converted = converted.replace(/\btú\b/g, 'el paciente');

    // Informal possessive "Tu/Tus" -> "del paciente"
    converted = converted.replace(/\bTu\b/g, 'Del paciente');
    converted = converted.replace(/\btu\b/g, 'del paciente');
    converted = converted.replace(/\bTus\b/g, 'Del paciente');
    converted = converted.replace(/\btus\b/g, 'del paciente');

    // Capitalize first letter if replacement happened at start
    if (converted.startsWith('el paciente') || converted.startsWith('del paciente')) {
      converted = converted.charAt(0).toUpperCase() + converted.slice(1);
    }
  } else {
    // English replacements
    // Case-insensitive matching for "Your" -> "The patient's"
    // We need to handle "Your" at start of sentence vs "your" in middle.

    // Replace "Your" / "your"
    converted = converted.replace(/\bYour\b/g, 'The patient\'s');
    converted = converted.replace(/\byour\b/g, 'the patient\'s');

    // Replace "You" / "you"
    // "Are you" -> "Is the patient" is tricky grammatically ("Are" -> "Is").
    // "Do you" -> "Does the patient" ("Do" -> "Does").
    // For now, we'll just stick to noun replacement and hope grammar isn't too broken,
    // or users will use thirdPartyLabel for complex sentences.

    converted = converted.replace(/\bYou\b/g, 'The patient');
    converted = converted.replace(/\byou\b/g, 'the patient');

    // Fix common verb agreement issues caused by "The patient" being singular vs "You"
    // This is very heuristic and won't catch everything.
    converted = converted.replace(/\bAre the patient\b/gi, 'Is the patient');
    converted = converted.replace(/\bDo the patient\b/gi, 'Does the patient');
    converted = converted.replace(/\bHave the patient\b/gi, 'Has the patient');

    // Capitalize first letter if replacement happened at start
    if (converted.startsWith('the patient')) {
      converted = `The patient${converted.slice(11)}`;
    }
  }

  return converted;
}

/**
 * Converts "The patient/The patient's" to "You/Your" for first-person mode.
 * Use this when survey text is stored in third-person format but user is filling for themselves.
 */
export function convertTextForFirstPerson(text: string | null | undefined, locale = 'en'): string {
  if (!text) return '';

  let converted = text;

  if (locale === 'ko') {
    // Korean: "환자분" -> implicit subject or "본인"
    converted = converted.replace(/환자분은/g, '');
    converted = converted.replace(/환자분의/g, '');
    converted = converted.replace(/환자분을/g, '');
    converted = converted.replace(/환자분에게/g, '');
    converted = converted.replace(/환자분/g, '');

    // Clean up extra spaces
    converted = converted.replace(/\s+/g, ' ').trim();
  } else if (locale === 'ja') {
    // Japanese: "患者様" -> "あなた" or implicit subject
    converted = converted.replace(/患者様は/g, 'あなたは');
    converted = converted.replace(/患者様の/g, 'あなたの');
    converted = converted.replace(/患者様を/g, 'あなたを');
    converted = converted.replace(/患者様に/g, 'あなたに');
    converted = converted.replace(/患者様/g, 'あなた');
  } else if (locale === 'es') {
    // Spanish: "El paciente" -> "Usted", "del paciente" -> "Su"
    converted = converted.replace(/\bEl paciente\b/g, 'Usted');
    converted = converted.replace(/\bel paciente\b/g, 'usted');

    converted = converted.replace(/\bDel paciente\b/g, 'Su');
    converted = converted.replace(/\bdel paciente\b/g, 'su');

    // Capitalize first letter if needed
    if (converted.startsWith('usted') || converted.startsWith('su ')) {
      converted = converted.charAt(0).toUpperCase() + converted.slice(1);
    }
  } else {
    // English: "The patient's" -> "Your", "The patient" -> "You"

    // Handle possessive first (longer match)
    converted = converted.replace(/\bThe patient's\b/g, 'Your');
    converted = converted.replace(/\bthe patient's\b/g, 'your');
    converted = converted.replace(/\bThe Patient's\b/g, 'Your');

    // Handle subject
    converted = converted.replace(/\bThe patient\b/g, 'You');
    converted = converted.replace(/\bthe patient\b/g, 'you');
    converted = converted.replace(/\bThe Patient\b/g, 'You');

    // Fix verb agreement: "Is the patient" -> "Are you", etc.
    converted = converted.replace(/\bIs you\b/gi, 'Are you');
    converted = converted.replace(/\bDoes you\b/gi, 'Do you');
    converted = converted.replace(/\bHas you\b/gi, 'Have you');

    // Capitalize first letter if needed
    if (converted.startsWith('you ') || converted.startsWith('your ')) {
      converted = converted.charAt(0).toUpperCase() + converted.slice(1);
    }
  }

  return converted;
}

